// src/components/CollectionIntakeCard.jsx
// Parcours d'accueil d'une collection : quatre questions de cadrage. Les réponses sont
// enregistrées dans collection_intake ; la dernière permet de poser une destination par défaut
// sur les œuvres qui n'en ont pas encore (champ artworks.destination).
// Aucune donnée d'identité ici : l'e-mail reste dans auth.users.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";

const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

const GOALS = ["conserve", "transmit", "sell", "donate", "undecided"];
const WEALTH = ["marginal", "significant", "major", "dominant", "unspecified"];
const DESTINATIONS = ["keep", "sell", "transmit", "donate", "pending"];

const GOAL_FR = {
  conserve: "La conserver et en profiter",
  transmit: "La transmettre à la famille",
  sell: "En vendre une partie",
  donate: "En donner ou en léguer à une institution",
  undecided: "Je ne sais pas encore",
};
const WEALTH_FR = {
  marginal: "Marginale (moins de 5 %)",
  significant: "Significative (5 à 25 %)",
  major: "Majeure (25 à 50 %)",
  dominant: "Dominante (plus de 50 %)",
  unspecified: "Je préfère ne pas préciser",
};

const labelStyle = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 };
const fieldStyle = { width: "100%", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, background: "#fff" };

export default function CollectionIntakeCard({ collectionId, ownerId, onSaved }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = ownerId || user?.id || null;
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ goal: "", wishes: "", wealth_share: "", default_destination: "" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    if (!collectionId) return undefined;
    (async () => {
      try {
        const { data } = await supabase
          .from("collection_intake")
          .select("goal, wishes, wealth_share, default_destination")
          .eq("collection_id", collectionId)
          .maybeSingle();
        if (!alive) return;
        if (data) {
          setForm({
            goal: data.goal || "",
            wishes: data.wishes || "",
            wealth_share: data.wealth_share || "",
            default_destination: data.default_destination || "",
          });
          setSaved(true);
        }
      } catch {
        /* table absente ou accès refusé : la carte reste vide */
      }
      if (alive) setLoaded(true);
    })();
    return () => { alive = false; };
  }, [collectionId]);

  if (!collectionId || !clientId || !loaded) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setMessage("");
    const { error } = await supabase.from("collection_intake").upsert({
      collection_id: collectionId,
      client_id: clientId,
      goal: form.goal || null,
      wishes: form.wishes.trim() || null,
      wealth_share: form.wealth_share || null,
      default_destination: form.default_destination || null,
      updated_at: new Date().toISOString(),
    });
    if (error) {
      setMessage(t("intake.saveError", { defaultValue: "Enregistrement impossible pour le moment." }));
      setBusy(false);
      return;
    }
    let applied = 0;
    if (form.default_destination) {
      const { data } = await supabase
        .from("artworks")
        .update({ destination: form.default_destination })
        .eq("collection_id", collectionId)
        .is("destination", null)
        .select("id");
      applied = data?.length || 0;
    }
    setSaved(true);
    setBusy(false);
    if (onSaved) onSaved();
    setMessage(
      applied > 0
        ? t("intake.savedApplied", { count: applied, defaultValue: `Réponses enregistrées. Destination appliquée à ${applied} œuvre(s) sans destination.` })
        : t("intake.saved", { defaultValue: "Réponses enregistrées." })
    );
  };

  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, background: "#faf9f7", marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: GOLD, marginBottom: 2 }}>
            {t("intake.eyebrow", { defaultValue: "Cadrage" })}
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1a1a", margin: 0, fontFamily: "Georgia, serif" }}>
            {t("intake.title", { defaultValue: "Que doit accomplir cette collection ?" })}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{ border: `1px solid ${BORDER}`, background: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12.5, cursor: "pointer" }}
        >
          {open
            ? t("intake.close", { defaultValue: "Fermer" })
            : saved
              ? t("intake.edit", { defaultValue: "Modifier" })
              : t("intake.start", { defaultValue: "Répondre (4 questions)" })}
        </button>
      </div>

      {!open && saved && form.goal && (
        <p style={{ fontSize: 12.5, color: "#6b6459", margin: "8px 0 0" }}>
          {t(`intake.goals.${form.goal}`, { defaultValue: GOAL_FR[form.goal] })}
        </p>
      )}

      {open && (
        <div style={{ marginTop: 14, display: "grid", gap: 14 }}>
          <div>
            <label style={labelStyle}>{t("intake.q1", { defaultValue: "1. Que doit accomplir cette collection ?" })}</label>
            <select value={form.goal} onChange={(e) => set("goal", e.target.value)} style={fieldStyle}>
              <option value="">{t("intake.select", { defaultValue: "Choisir…" })}</option>
              {GOALS.map((k) => <option key={k} value={k}>{t(`intake.goals.${k}`, { defaultValue: GOAL_FR[k] })}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t("intake.q2", { defaultValue: "2. Que souhaitent la famille et les institutions concernées ?" })}</label>
            <textarea
              rows={3}
              value={form.wishes}
              onChange={(e) => set("wishes", e.target.value)}
              placeholder={t("intake.q2Placeholder", { defaultValue: "Attentes, souhaits exprimés, musées ou fondations approchés…" })}
              style={{ ...fieldStyle, resize: "vertical" }}
            />
          </div>
          <div>
            <label style={labelStyle}>{t("intake.q3", { defaultValue: "3. Quelle place dans votre patrimoine global ?" })}</label>
            <select value={form.wealth_share} onChange={(e) => set("wealth_share", e.target.value)} style={fieldStyle}>
              <option value="">{t("intake.select", { defaultValue: "Choisir…" })}</option>
              {WEALTH.map((k) => <option key={k} value={k}>{t(`intake.wealth.${k}`, { defaultValue: WEALTH_FR[k] })}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t("intake.q4", { defaultValue: "4. Quel rôle pour les œuvres, par défaut ?" })}</label>
            <select value={form.default_destination} onChange={(e) => set("default_destination", e.target.value)} style={fieldStyle}>
              <option value="">{t("intake.select", { defaultValue: "Choisir…" })}</option>
              {DESTINATIONS.map((k) => <option key={k} value={k}>{t(`artworkEdit.detail.destinationOptions.${k}`)}</option>)}
            </select>
            <p style={{ fontSize: 11.5, color: "#8a8378", margin: "4px 0 0" }}>
              {t("intake.q4Hint", { defaultValue: "S'applique uniquement aux œuvres qui n'ont pas encore de destination ; vous pourrez ajuster œuvre par œuvre." })}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              type="button"
              disabled={busy}
              onClick={save}
              style={{ background: GOLD, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}
            >
              {t("intake.save", { defaultValue: "Enregistrer" })}
            </button>
            {message && <span style={{ fontSize: 12.5, color: "#6b6459" }}>{message}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
